import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Package, User, ShieldCheck, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const DashboardPage = () => {
  const { user, fetchUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyingAffiliate, setApplyingAffiliate] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, kycRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/kyc/status`)
      ]);
      setOrders(ordersRes.data);
      setKycStatus(kycRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAffiliate = async () => {
    setApplyingAffiliate(true);
    try {
      await axios.post(`${API}/affiliate/apply`, {
        reason: "I want to earn commissions by referring customers"
      });
      toast.success("Affiliate application submitted!");
      await fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Application failed");
    } finally {
      setApplyingAffiliate(false);
    }
  };

  const getStatusBadge = (status) => {
    return <span className={`status-badge ${status}`}>{status}</span>;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Dashboard</p>
            <h1 className="text-4xl font-bold text-white">Welcome, {user?.name}</h1>
          </div>

          {!user?.is_affiliate && (
            <Button
              onClick={handleApplyAffiliate}
              disabled={applyingAffiliate}
              className="btn-outline flex items-center gap-2"
              data-testid="apply-affiliate-btn"
            >
              {applyingAffiliate ? (
                <div className="spinner w-4 h-4" />
              ) : (
                <>
                  <Users className="w-4 h-4" /> Become an Affiliate
                </>
              )}
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-orders">
            <div className="stat-value">{orders.length}</div>
            <div className="stat-label">Total Orders</div>
          </div>
          <div className="stat-card" data-testid="stat-paid">
            <div className="stat-value">{orders.filter(o => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status)).length}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card" data-testid="stat-kyc">
            <div className="stat-value capitalize">{kycStatus?.status || 'Pending'}</div>
            <div className="stat-label">KYC Status</div>
          </div>
          <div className="stat-card" data-testid="stat-affiliate">
            <div className="stat-value">{user?.is_affiliate ? 'Active' : 'None'}</div>
            <div className="stat-label">Affiliate</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-[#121212] border border-[rgba(255,255,255,0.05)]">
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Package className="w-4 h-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <User className="w-4 h-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="kyc" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <ShieldCheck className="w-4 h-4 mr-2" /> KYC
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="orders-tab">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-[#121212] h-24 animate-pulse" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <Package className="empty-state-icon" />
                <h3 className="text-xl text-white mb-2">No orders yet</h3>
                <p className="text-[#737373] mb-4">Start shopping to see your orders here</p>
                <Link to="/products" className="btn-gold inline-flex items-center gap-2">
                  Shop Now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="dashboard-card"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-white font-medium">Order #{order.id.slice(0, 8)}</p>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-[#737373]">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-sm text-[#A3A3A3] mt-1">
                          {order.items.length} item(s)
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-light text-[#D4AF37]">
                          ₹{order.total?.toLocaleString()}
                        </p>
                        <p className="text-xs text-[#737373]">
                          {order.shipping_city}, {order.shipping_state}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" data-testid="profile-tab">
            <div className="dashboard-card max-w-2xl">
              <h3 className="text-lg font-semibold text-white mb-6">Account Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.05)]">
                  <span className="text-[#A3A3A3]">Name</span>
                  <span className="text-white">{user?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.05)]">
                  <span className="text-[#A3A3A3]">Email</span>
                  <span className="text-white">{user?.email}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.05)]">
                  <span className="text-[#A3A3A3]">Phone</span>
                  <span className="text-white">{user?.phone}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.05)]">
                  <span className="text-[#A3A3A3]">Member Since</span>
                  <span className="text-white">
                    {new Date(user?.created_at).toLocaleDateString('en-IN', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                {user?.is_affiliate && (
                  <div className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.05)]">
                    <span className="text-[#A3A3A3]">Affiliate Code</span>
                    <span className="text-[#D4AF37] font-mono">{user?.affiliate_code}</span>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc" data-testid="kyc-tab">
            <div className="dashboard-card max-w-2xl">
              <h3 className="text-lg font-semibold text-white mb-6">KYC Verification</h3>
              
              <div className="flex items-center gap-3 mb-6 p-4 bg-[#0a0a0a]">
                <ShieldCheck className={`w-6 h-6 ${kycStatus?.status === 'verified' ? 'text-[#00E676]' : 'text-[#D4AF37]'}`} />
                <div>
                  <p className="text-white font-medium capitalize">Status: {kycStatus?.status || 'Pending'}</p>
                  <p className="text-sm text-[#737373]">
                    {kycStatus?.status === 'verified' 
                      ? 'Your KYC is verified. You can place orders.'
                      : 'Complete KYC verification at checkout to place orders.'}
                  </p>
                </div>
              </div>

              {kycStatus?.documents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Submitted Documents</h4>
                  <div className="space-y-2">
                    {kycStatus.documents.map((doc, index) => (
                      <div key={index} className="flex justify-between items-center py-3 border-b border-[rgba(255,255,255,0.05)]">
                        <div>
                          <p className="text-white capitalize">{doc.type} Card</p>
                          <p className="text-sm text-[#737373]">{doc.number}</p>
                        </div>
                        <span className={`status-badge ${doc.status === 'verified' ? 'delivered' : 'pending'}`}>
                          {doc.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DashboardPage;
